// xlsx is ~1MB minified and only needed when a user imports/exports Excel —
// every function loads it on demand so it stays out of the main bundle

/**
 * Read an Excel file and convert it to CSV format
 */
export async function readExcelFileAsCSV(file: File): Promise<string> {
  const XLSX = await import('xlsx');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = e => {
      try {
        const data = e.target?.result as ArrayBuffer;
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to CSV
        const csvData = XLSX.utils.sheet_to_csv(worksheet, {
          skipHidden: false,
          blankrows: false,
        });

        resolve(csvData);
      } catch (error) {
        reject(
          new Error(
            `Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`
          )
        );
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * Read an Excel file and return structured data
 */
export async function readExcelFileAsJSON(file: File): Promise<{
  headers: string[];
  rows: Record<string, any>[];
}> {
  const XLSX = await import('xlsx');
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = e => {
      try {
        const data = e.target?.result as ArrayBuffer;
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON with header row
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          skipHidden: false,
          blankrows: false,
          defval: '', // Default value for empty cells
        }) as string[][];

        if (jsonData.length === 0) {
          resolve({ headers: [], rows: [] });
          return;
        }

        // First row contains headers
        const headers = jsonData[0]
          .map(header => String(header).trim())
          .filter(h => h);
        const dataRows = jsonData.slice(1);

        // Convert rows to objects
        const rows = dataRows
          .map(row => {
            const rowObj: Record<string, any> = {};
            headers.forEach((header, index) => {
              const value = row[index];
              rowObj[header] =
                value !== undefined && value !== null
                  ? String(value).trim()
                  : '';
            });
            return rowObj;
          })
          .filter(row => {
            // Filter out completely empty rows
            return Object.values(row).some(value => value !== '');
          });

        resolve({ headers, rows });
      } catch (error) {
        reject(
          new Error(
            `Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`
          )
        );
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * Validate if a file is a valid Excel file
 */
export function isExcelFile(file: File): boolean {
  const validTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  const validExtensions = ['.xls', '.xlsx'];

  return (
    validTypes.includes(file.type) ||
    validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
  );
}

/**
 * Get file info for validation
 */
export function getExcelFileInfo(file: File): {
  name: string;
  size: string;
  type: string;
  isValid: boolean;
} {
  const isValid = isExcelFile(file);
  const size = (file.size / 1024 / 1024).toFixed(2) + ' MB';

  return {
    name: file.name,
    size,
    type: file.type,
    isValid,
  };
}

/**
 * Export spreadsheet data to Excel file
 */
export async function exportToExcel(
  data: { headers: string[]; rows: Record<string, any>[] },
  filename: string = 'spreadsheet'
): Promise<void> {
  const XLSX = await import('xlsx');
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // SECURITY: Sanitize data to prevent CSV/Formula injection
    const sanitizeCell = (value: any): string => {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      // Handle objects (like JSONB data) - convert to JSON string
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return '[Complex Data]';
        }
      }

      const stringValue = String(value);

      // If value starts with =, +, -, @, tab, or carriage return, prefix with single quote
      // This prevents formula execution in Excel/Google Sheets
      if (/^[=+\-@\t\r]/.test(stringValue)) {
        return "'" + stringValue;
      }

      return stringValue;
    };

    // Prepare data for Excel with sanitization
    const excelData = [
      data.headers, // Header row
      ...data.rows.map(row =>
        data.headers.map(header => sanitizeCell(row[header]))
      ),
    ];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths for better readability
    const columnWidths = data.headers.map(header => ({
      wch: Math.max(header.length, 15), // Minimum width of 15 characters
    }));
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

    // Generate filename with a local timestamp (toISOString is UTC and
    // shifts the date after ~6pm Central)
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const timestamp =
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
      `T${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    const finalFilename = `${filename}-${timestamp}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, finalFilename);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export spreadsheet to Excel');
  }
}
